/**
 * Lukujen kieliasu (WP-KL3, CRM-harjoituksen löydös `E-3`).
 *
 * <p>`metadata.locale` ohjasi ennen vain validointiviestejä ja latausriviä, joten
 * suomenkielinen App näytti tunnusluvun muodossa `€1,234,567`. Suomalaiselle
 * lukijalle se on kolme virhettä kolmessa merkissä: symboli on väärässä päässä,
 * ryhmitin on pilkku ja välilyönti puuttuu. Raha on CRM:n jokaisella ruudulla,
 * joten tämä ei ole kosmetiikkaa.
 *
 * <p><b>Ei `Intl`-riippuvuutta.</b> Runtime on kehysriippumaton bundle
 * sandbox-iframessa, ja `stat.ts`/`dataGrid.ts` ovat tarkoituksella
 * deterministisiä ympäristöstä riippumatta — `Intl.NumberFormat` toisi mukanaan
 * selaimen ja ajoympäristön lokaalidatan, jolloin sama App näyttäisi eri luvun
 * eri koneilla. Kaksi kieltä, kaksi tyyliä, ei enempää.
 *
 * <p>Ryhmitin on **sitova välilyönti** (U+00A0): tavallinen välilyönti sallisi
 * rivinvaihdon luvun keskeltä taulukon solussa.
 */
import type { MessageLocale } from './messages';

/** Yhden kielen numerotyyli. */
export interface NumberStyle {
  /** Tuhaterotin. */
  group: string;
  /** Desimaalierotin. */
  decimal: string;
  /** Tuleeko valuuttasymboli luvun eteen (`€1 234`) vai perään (`1 234 €`). */
  currencyBefore: boolean;
  /** Erotin luvun ja valuuttasymbolin/prosenttimerkin välissä. */
  unitSpace: string;
}

const NBSP = '\u00A0';

const STYLES: Record<MessageLocale, NumberStyle> = {
  en: { group: ',', decimal: '.', currencyBefore: true, unitSpace: '' },
  fi: { group: NBSP, decimal: ',', currencyBefore: false, unitSpace: NBSP },
};

/**
 * Valuuttasymboli on kiinteä euro (`D3` = A). Konfiguroitava valuutta on
 * additiivinen lisäys jos joskus tarvitaan; yksikään neljästä harjoituksesta ei
 * ole tarvinnut muuta, ja `metadata.currency` toisi mukanaan kysymyksen
 * kurssimuunnoksista jota kukaan ei ole kysynyt.
 */
export const CURRENCY_SYMBOL = '€';

export function numberStyle(locale: MessageLocale): NumberStyle {
  return STYLES[locale] ?? STYLES.en;
}

/** Ryhmittele ei-negatiivinen numerojono tuhaterottimin. */
export function groupDigits(intStr: string, style: NumberStyle): string {
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, style.group);
}

/** Kokonaisluku ryhmiteltynä (pyöristäen). */
export function formatInteger(n: number, style: NumberStyle): string {
  const neg = n < 0;
  return (neg ? '-' : '') + groupDigits(Math.abs(Math.round(n)).toString(), style);
}

/** Luku jossa on desimaaliosa; `decimals` = kiinteä määrä, tai null = säilytä. */
export function formatDecimal(n: number, style: NumberStyle, decimals: number | null): string {
  const neg = n < 0;
  const abs = Math.abs(n);
  const fixed = decimals == null ? abs.toString() : abs.toFixed(decimals);
  const [ip, dp] = fixed.split('.');
  return (neg ? '-' : '') + groupDigits(ip, style) + (dp ? style.decimal + dp : '');
}

/** Valuutta kielen mukaisessa järjestyksessä. */
export function formatCurrency(n: number, style: NumberStyle): string {
  const body = formatInteger(n, style);
  return style.currencyBefore
    ? CURRENCY_SYMBOL + body
    : body + style.unitSpace + CURRENCY_SYMBOL;
}

/**
 * Prosentti. Syöte on jo näyttöyksikkö (12 → "12 %"), ei osuus.
 * Suomessa prosenttimerkin eteen kuuluu välilyönti, englannissa ei.
 */
export function formatPercent(n: number, style: NumberStyle): string {
  const rounded = Math.round(n * 100) / 100;
  return formatDecimal(rounded, style, null) + style.unitSpace + '%';
}
